/**
 * Core Popover Component
 *
 * Click-triggered floating content panel with anchor element,
 * placement, arrow, and controlled/uncontrolled modes.
 *
 * @example
 *   <Popover
 *     trigger={<Button variant="ghost">Open Menu</Button>}
 *     placement="bottom-start"
 *   >
 *     <div className="p-3">Popover content</div>
 *   </Popover>
 */
import React, { useState, useRef } from 'react';
import { Popover as MuiPopover, Box } from '@mui/material';

export interface PopoverProps {
  /** Trigger element — click to open */
  trigger: React.ReactNode;
  /** Content */
  children: React.ReactNode;
  /** Anchor placement origin */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  /** Controlled open state */
  open?: boolean;
  /** Controlled change handler */
  onOpenChange?: (open: boolean) => void;
  /** Disable */
  disabled?: boolean;
  /** Paper width */
  width?: string | number;
  /** Paper max height */
  maxHeight?: string | number;
  /** Custom offset from anchor */
  offset?: number;
  className?: string;
}

const placementMap: Record<string, { anchorOrigin: any; transformOrigin: any }> = {
  'bottom':       { anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, transformOrigin: { vertical: 'top', horizontal: 'center' } },
  'bottom-start': { anchorOrigin: { vertical: 'bottom', horizontal: 'left' },   transformOrigin: { vertical: 'top', horizontal: 'left' } },
  'bottom-end':   { anchorOrigin: { vertical: 'bottom', horizontal: 'right' },  transformOrigin: { vertical: 'top', horizontal: 'right' } },
  'top':          { anchorOrigin: { vertical: 'top', horizontal: 'center' },    transformOrigin: { vertical: 'bottom', horizontal: 'center' } },
  'top-start':    { anchorOrigin: { vertical: 'top', horizontal: 'left' },      transformOrigin: { vertical: 'bottom', horizontal: 'left' } },
  'top-end':      { anchorOrigin: { vertical: 'top', horizontal: 'right' },     transformOrigin: { vertical: 'bottom', horizontal: 'right' } },
  'left':         { anchorOrigin: { vertical: 'center', horizontal: 'left' },   transformOrigin: { vertical: 'center', horizontal: 'right' } },
  'right':        { anchorOrigin: { vertical: 'center', horizontal: 'right' },  transformOrigin: { vertical: 'center', horizontal: 'left' } },
};

export const Popover: React.FC<PopoverProps> = ({
  trigger,
  children,
  placement = 'bottom-start',
  open: controlledOpen,
  onOpenChange,
  disabled = false,
  width,
  maxHeight,
  offset = 4,
  className = '',
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const isOpen = controlledOpen ?? internalOpen;

  const handleToggle = () => {
    if (disabled) return;
    const next = !isOpen;
    if (controlledOpen === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const handleClose = () => {
    if (controlledOpen === undefined) setInternalOpen(false);
    onOpenChange?.(false);
  };

  const { anchorOrigin, transformOrigin } = placementMap[placement] || placementMap['bottom-start'];

  return (
    <>
      <Box ref={anchorRef} onClick={handleToggle} sx={{ display: 'inline-flex', cursor: disabled ? 'default' : 'pointer' }}>
        {trigger}
      </Box>
      <MuiPopover
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        className={className}
        slotProps={{
          paper: {
            sx: {
              mt: offset / 8,
              borderRadius: '12px',
              minWidth: width || 200,
              maxHeight: maxHeight || 400,
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            },
          },
        }}
      >
        {children}
      </MuiPopover>
    </>
  );
};
export default Popover;
